import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { accountGroupsApi, ApiError, ledgersApi, type LedgerWritePayload, type UpdateLedgerPayload } from '../../lib/api';
import { GroupTree } from './GroupTree';
import { LedgerForm } from './LedgerForm';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { TypeAhead, type TypeAheadItem } from '../../components/TypeAhead';
import { Modal } from '../../components/Modal';
import { ErrorBanner } from '../../components/ErrorBanner';
import { EmptyState } from '../../components/EmptyState';
import { Can } from '../../lib/permissions';
import { useShortcuts } from '../../lib/keys';
import { useUiStore } from '../../stores/ui';
import { useCompanyStore } from '../../stores/company';
import { formatIndian } from '../../lib/money';
import { EditLogPanel } from '../audit/EditLogPanel';
import type { AccountGroup, Ledger } from '../../lib/types';

function flattenGroups(nodes: AccountGroup[], out: AccountGroup[] = []): AccountGroup[] {
  for (const g of nodes) {
    out.push(g);
    if (g.children) flattenGroups(g.children, out);
  }
  return out;
}

function descendantIds(groups: AccountGroup[], rootId: string): Set<string> {
  const byParent = new Map<string | null, AccountGroup[]>();
  for (const g of groups) {
    const list = byParent.get(g.parentId) ?? [];
    list.push(g);
    byParent.set(g.parentId, list);
  }
  const ids = new Set<string>([rootId]);
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop()!;
    for (const child of byParent.get(id) ?? []) {
      ids.add(child.id);
      stack.push(child.id);
    }
  }
  return ids;
}

export function ChartOfAccountsPage() {
  const [search] = useSearchParams();
  const [tree, setTree] = useState<AccountGroup[]>([]);
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedLedger, setSelectedLedger] = useState<Ledger | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Ledger | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const pushToast = useUiStore((s) => s.pushToast);
  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId);

  useEffect(() => {
    let cancelled = false;
    setTree([]);
    setLedgers([]);
    setSelectedGroupId(null);
    setSelectedLedger(null);
    setEditing(null);
    setError(null);
    if (!activeCompanyId) return () => { cancelled = true; };
    Promise.all([accountGroupsApi.tree(), ledgersApi.list()])
      .then(([groupResponse, ledgerResponse]) => {
        if (cancelled) return;
        setTree(groupResponse.items);
        setLedgers(ledgerResponse.items);
      })
      .catch((cause) => { if (!cancelled) setError(cause); });
    return () => { cancelled = true; };
  }, [activeCompanyId]);

  const flatGroups = useMemo(() => flattenGroups(tree), [tree]);

  const visibleLedgers = useMemo(() => {
    if (!selectedGroupId) return ledgers;
    const ids = descendantIds(flatGroups, selectedGroupId);
    return ledgers.filter((l) => ids.has(l.groupId));
  }, [ledgers, flatGroups, selectedGroupId]);

  const jumpItems: TypeAheadItem[] = ledgers.map((l) => ({
    key: l.id,
    label: l.name,
    sublabel: flatGroups.find((g) => g.id === l.groupId)?.name,
  }));

  useShortcuts('Chart of accounts', [
    { id: 'new-ledger', bindings: [{ key: 'n', alt: true }], description: 'New ledger', handler: () => { setEditing(null); setFormOpen(true); } },
  ]);

  function openLedger(ledger: Ledger) {
    setSelectedLedger(ledger);
    setEditing(ledger);
    setFormOpen(true);
  }

  async function handleSave(payload: LedgerWritePayload | UpdateLedgerPayload) {
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        const updated = await ledgersApi.update(editing.id, payload as UpdateLedgerPayload);
        setLedgers((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
        pushToast({ kind: 'success', message: `${updated.name} saved.` });
      } else {
        const created = await ledgersApi.create(payload);
        setLedgers((prev) => [...prev, created]);
        pushToast({ kind: 'success', message: `${created.name} created.` });
      }
      setFormOpen(false);
    } catch (err) {
      if (err instanceof ApiError && err.isRevisionConflict) {
        setError(err);
      } else {
        setError(err);
      }
    } finally {
      setSaving(false);
    }
  }

  const columns: DataTableColumn<Ledger>[] = [
    { key: 'name', header: 'Ledger', render: (l) => <span>{l.name}{l.isSystem && <span className="ml-1.5 kbd">system</span>}</span> },
    { key: 'group', header: 'Group', render: (l) => <span className="text-[var(--text-dim)]">{flatGroups.find((g) => g.id === l.groupId)?.name ?? '—'}</span> },
    { key: 'type', header: 'Type', render: (l) => l.ledgerType },
    {
      key: 'gst', header: 'GST status',
      render: (l) => l.ledgerType === 'party'
        ? <span className="text-[var(--text-dim)]">{l.registrationType ? l.registrationType.replaceAll('_', ' ') : 'Needs GST status'}{l.gstin ? ` · ${l.gstin}` : ''}</span>
        : '—',
    },
    {
      key: 'balance', header: 'Opening balance', numeric: true,
      render: (l) => `${formatIndian(l.openingBalance)} ${l.openingBalanceType === 'dr' ? 'Dr' : 'Cr'}`,
    },
  ];

  return (
    <div className="flex h-full">
      <aside className="flex w-64 shrink-0 flex-col border-r border-[var(--border)] bg-[var(--bg-elevated)]">
        <div className="border-b border-[var(--border)] px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">
          Account groups
        </div>
        <GroupTree tree={tree} selectedId={selectedGroupId} onSelect={setSelectedGroupId} />
      </aside>

      <section className="flex min-w-0 flex-1 flex-col p-4">
        <div className="mb-3 flex items-center justify-between border-b border-[var(--border)] pb-2">
          <h1 className="text-[14px] font-semibold">Chart of accounts</h1>
          <Can permission="master:ledger:create">
            <button
              onClick={() => { setEditing(null); setFormOpen(true); }}
              className="rounded-[3px] border border-[var(--border)] px-2 py-1 text-[12px] hover:bg-[var(--bg-hover)]"
            >
              New ledger <span className="kbd ml-1">Alt+N</span>
            </button>
          </Can>
        </div>

        <div className="mb-2">
          <TypeAhead
            items={jumpItems}
            onSelect={(item) => {
              const ledger = ledgers.find((l) => l.id === item.key);
              if (ledger) openLedger(ledger);
            }}
            placeholder="Jump to ledger by name, alias…"
          />
        </div>

        {search.get('reportSource') === 'opening_configuration' && <div className="mb-2 flex items-center justify-between border border-[var(--warn)] bg-[var(--warn-bg)] px-3 py-2 text-[12px] text-[var(--warn)]"><span><b>Opening-balance configuration</b> — the report disclosed a difference in independently configured ledger openings. Review the opening Dr/Cr values here; this is not a voucher.</span></div>}
        {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}

        <div className="min-h-0 flex-1">
          {visibleLedgers.length === 0 ? (
            <EmptyState title="No ledgers in this group." hint="Create one with Alt+N." />
          ) : (
            <DataTable
              columns={columns}
              rows={visibleLedgers}
              getRowKey={(l) => l.id}
              onActivate={openLedger}
              className="h-full"
            />
          )}
        </div>
        <p className="mt-1 text-[11px] text-[var(--text-faint)]">
          ↑↓ to move, Enter to edit the highlighted ledger. {visibleLedgers.length} of {ledgers.length} ledgers shown.
        </p>
      </section>

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={editing ? `Edit — ${editing.name}` : 'New ledger'} widthClassName="max-w-xl">
        <LedgerForm groups={flatGroups} initial={editing} onSave={handleSave} onCancel={() => setFormOpen(false)} busy={saving} error={error} />
        {editing && (
          <button
            onClick={() => { setLogOpen(true); }}
            className="mt-3 text-[11.5px] text-[var(--accent)] underline underline-offset-2"
          >
            View edit log for this ledger
          </button>
        )}
      </Modal>

      <Modal open={logOpen} onClose={() => setLogOpen(false)} title={`Edit log — ${selectedLedger?.name ?? ''}`} widthClassName="max-w-2xl">
        {selectedLedger && <EditLogPanel table="ledgers" recordId={selectedLedger.id} />}
      </Modal>
    </div>
  );
}
